import React, { FC, useRef, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useDanceStore, MIRROR_MAP, calculateGroundedElevation, defaultPose, JOINT_PARENT_MAP } from '../store/useDanceStore';
import { JOINT_CONSTRAINTS, clamp, Range } from '../utils/constraints';
import { RotateCcw, Tag, Axis3d, ChevronDown, ArrowLeftRight, Minus, Square, RefreshCcw, Save, Trash2, ArrowRight, Link, Footprints, Compass, MapPin, Target, ArrowDown, ArrowUp, Undo, Redo, FlipHorizontal2, Bot, Clipboard, FolderOpen, Edit3, AlertCircle, Library, PersonStanding } from 'lucide-react';
import { useStorageStore } from '../store/useStorageStore';
import { useTranslation } from 'react-i18next';
import { DanceState, StorageState } from '../types';
import { executeRecaptcha } from '../utils/recaptcha';
import { generatePoseFromText, fetchPoseDataFromText, applyPoseData } from '../modules/TextToPoseModule';

interface JointControlProps {
  label: string;
  jointName: string;
  axes: string[];
  showGround?: boolean;
  vertical?: boolean;
  hideAxes?: boolean;
  hideTitle?: boolean;
  children?: React.ReactNode;
}

const JointControl: FC<JointControlProps> = ({ label, jointName, axes, showGround, vertical, hideAxes, hideTitle, children }) => {
  const { t } = useTranslation();
  const values = useDanceStore(
    useShallow((state: DanceState) => (state.currentPose[jointName] || [0, 0, 0]) as [number, number, number])
  );
  const setJointRotation = useDanceStore((state: DanceState) => state.setJointRotation);
  const groundDancer = useDanceStore((state: DanceState) => state.groundDancer);
  const selectedJoint = useDanceStore((state: DanceState) => state.selectedJoint);
  const isMirroring = useDanceStore((state: DanceState) => state.isMirroring);
  const activeAxis = useDanceStore((state: DanceState) => state.activeAxis);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const commitHistory = useDanceStore((state: DanceState) => state.commitHistory);
  const setIsPosing = useDanceStore((state: DanceState) => state.setIsPosing);

  const setSelectedJoint = useDanceStore((state: DanceState) => state.setSelectedJoint);
  const isReadOnly = useDanceStore((state: DanceState) => state.isReadOnly);

  const isSelected = selectedJoint === jointName;
  const isSymmetryGlow = isMirroring && selectedJoint && MIRROR_MAP[selectedJoint] === jointName;
  const mirroredJoint = isMirroring && selectedJoint ? MIRROR_MAP[selectedJoint] : null;
  const isParentOfSelected = selectedJoint ? JOINT_PARENT_MAP[selectedJoint] === jointName : false;
  const isParentOfMirrored = (isMirroring && mirroredJoint) ? JOINT_PARENT_MAP[mirroredJoint] === jointName : false;
  const isParentHighlight = isParentOfSelected || isParentOfMirrored;

  const limits: Range = JOINT_CONSTRAINTS[jointName] || { x: [0,0], y: [0,0], z: [0,0] };
  
  // Only subscribe to currentElevY and groundElev if needed
  const currentElevY = useDanceStore((state: DanceState) => (state.currentPose.elevation || [0,0,0])[1]);
  const groundElev = useDanceStore((state: DanceState) => state.groundedElevation);
  const isGrounded = Math.abs(currentElevY - groundElev) < 0.01;

  if (jointName === 'elevation') {
    limits.y = [groundElev, 3.0];
  }

  const [localTexts, setLocalTexts] = React.useState<[string, string, string]>(['', '', '']);
  const isFocusedRef = useRef<[boolean, boolean, boolean]>([false, false, false]);

  // Sync store values to local text when they change (only if not focused)
  useEffect(() => {
    const newTexts = [...localTexts] as [string, string, string];
    let changed = false;
    for (let i = 0; i < 3; i++) {
      if (!isFocusedRef.current[i]) {
        const val = values[i];
        const formatted = jointName === 'elevation' ? val.toFixed(2) : String(Math.round(val));
        if (newTexts[i] !== formatted) {
          newTexts[i] = formatted;
          changed = true;
        }
      }
    }
    if (changed) {
      setLocalTexts(newTexts);
    }
  }, [values, jointName]);

  const handleSliderChange = (axisIndex: number, value: string) => {
    const newValues: [number, number, number] = [...values];
    const axisKey: 'x' | 'y' | 'z' = axisIndex === 0 ? 'x' : axisIndex === 1 ? 'y' : 'z';
    newValues[axisIndex] = clamp(Number(value), limits[axisKey][0], limits[axisKey][1]);
    setJointRotation(jointName, newValues[0], newValues[1], newValues[2]);
  };

  const handleTextChange = (axisIndex: number, textValue: string) => {
    const newTexts = [...localTexts] as [string, string, string];
    newTexts[axisIndex] = textValue;
    setLocalTexts(newTexts);

    const num = Number(textValue);
    if (!isNaN(num) && textValue.trim() !== '' && textValue !== '-' && textValue !== '.') {
      const axisKey: 'x' | 'y' | 'z' = axisIndex === 0 ? 'x' : axisIndex === 1 ? 'y' : 'z';
      const clampedVal = clamp(num, limits[axisKey][0], limits[axisKey][1]);
      
      const newRotations = [...values] as [number, number, number];
      newRotations[axisIndex] = clampedVal;
      setJointRotation(jointName, newRotations[0], newRotations[1], newRotations[2]);
    }
  };

  const handleTextBlur = (axisIndex: number) => {
    isFocusedRef.current[axisIndex] = false;
    
    const rawVal = localTexts[axisIndex];
    const num = Number(rawVal);
    const axisKey: 'x' | 'y' | 'z' = axisIndex === 0 ? 'x' : axisIndex === 1 ? 'y' : 'z';
    
    let finalVal = values[axisIndex];
    if (!isNaN(num) && rawVal.trim() !== '') {
      finalVal = clamp(num, limits[axisKey][0], limits[axisKey][1]);
    }
    
    const newRotations = [...values] as [number, number, number];
    newRotations[axisIndex] = finalVal;
    setJointRotation(jointName, newRotations[0], newRotations[1], newRotations[2]);
    
    const formatted = jointName === 'elevation' ? finalVal.toFixed(2) : String(Math.round(finalVal));
    const newTexts = [...localTexts] as [string, string, string];
    newTexts[axisIndex] = formatted;
    setLocalTexts(newTexts);
  };

  const handleKeyDown = (axisIndex: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div 
      id={`joint-control-${jointName}`}
      className={`control-group ${isSelected || isSymmetryGlow ? 'glowing' : isParentHighlight ? 'parent-glowing' : ''} ${vertical ? 'vertical-group' : ''}`} 
      style={{
        ...(vertical ? {padding: '2px'} : {}),
        cursor: isReadOnly ? 'default' : 'pointer'
      }}
      onClick={() => { if (!isReadOnly) setSelectedJoint(jointName); }}
    >
      {!vertical && !hideTitle && (
        <div className="control-label" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{label}</span>
          {showGround && (
            <button 
              className={`icon-button mini ${!isGrounded ? 'ground-highlight' : ''}`} 
              onClick={(e) => { e.stopPropagation(); groundDancer(); }}
              disabled={isPlaying}
              title={t('sidebar.ground_mannequin_tooltip')}
              style={{padding: '2px', height: 'auto', background: 'transparent', opacity: isPlaying ? 0.5 : 1}}
            >
              <ChevronDown size={14} />
            </button>
          )}
        </div>
      )}
      
      <div 
        className={vertical ? 'flex-col' : ''} 
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          width: '100%',
          ...(vertical ? { alignItems: 'center' } : {})
        }}
      >
        {vertical && showGround && (
          <button 
            className={`icon-button mini ${!isGrounded ? 'ground-highlight' : ''}`} 
            onClick={() => groundDancer()}
            disabled={isPlaying}
            title={t('sidebar.ground_mannequin')}
            style={{padding: '0', background: 'transparent', marginBottom: '8px', opacity: isPlaying ? 0.5 : 1}}
          >
            <ChevronDown size={16} color="#38bdf8" />
          </button>
        )}

        {axes.includes('X') && limits.x[0] !== limits.x[1] && (
          <div className={`flex-row ${(isSelected || isSymmetryGlow) && activeAxis === 'X' ? 'axis-glow' : ''}`}>
            {!hideAxes && <span style={{width: 15, fontSize: '0.65rem', color: '#ef4444', fontWeight: 'bold', lineHeight: '14px'}}>X</span>}
            <div className="joint-slider-container">
              <input 
                type="text" 
                className="joint-number-input"
                value={localTexts[0]}
                onChange={(e) => handleTextChange(0, e.target.value)}
                onFocus={() => {
                  isFocusedRef.current[0] = true;
                  if (!isReadOnly) setSelectedJoint(jointName);
                  commitHistory('Text Edit');
                  setIsPosing(true);
                }}
                onBlur={() => {
                  handleTextBlur(0);
                  setIsPosing(false);
                }}
                onKeyDown={(e) => handleKeyDown(0, e)}
                disabled={isPlaying}
                aria-label={t('sidebar.manual_angle_input', { axis: 'X' })}
              />
              <div className="slider-wrapper with-input">
                <input 
                  id={`joint-slider-${jointName}-X`}
                  type="range" min={limits.x[0]} max={limits.x[1]} value={values[0]} 
                  onChange={(e) => handleSliderChange(0, e.target.value)}
                  onPointerDown={() => {
                    commitHistory('Slider Edit');
                    setIsPosing(true);
                  }}
                  onPointerUp={() => setIsPosing(false)}
                  disabled={isPlaying}
                  style={{ opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer' }}
                />
                <div className="slider-value" style={{left: `calc(12px + (100% - 24px) * ${(values[0] - limits.x[0]) / (limits.x[1] - limits.x[0])})`}}>
                  {Math.round(values[0])}
                </div>
              </div>
            </div>
          </div>
        )}
        {axes.includes('Y') && limits.y[0] !== limits.y[1] && (
          <div className={`${vertical ? 'flex-col' : 'flex-row'} ${(isSelected || isSymmetryGlow) && activeAxis === 'Y' ? 'axis-glow' : ''}`}>
            {!vertical && !hideAxes && <span style={{width: 15, fontSize: '0.65rem', color: '#22c55e', fontWeight: 'bold', lineHeight: '14px'}}>Y</span>}
            <div className="joint-slider-container">
              <input 
                type="text" 
                className="joint-number-input"
                value={localTexts[1]}
                onChange={(e) => handleTextChange(1, e.target.value)}
                onFocus={() => {
                  isFocusedRef.current[1] = true;
                  if (!isReadOnly) setSelectedJoint(jointName);
                  commitHistory('Text Edit');
                  setIsPosing(true);
                }}
                onBlur={() => {
                  handleTextBlur(1);
                  setIsPosing(false);
                }}
                onKeyDown={(e) => handleKeyDown(1, e)}
                disabled={isPlaying}
                aria-label={t('sidebar.manual_angle_input', { axis: 'Y' })}
              />
              <div className={`slider-wrapper with-input ${vertical ? 'vertical-rotated' : ''}`}>
                <input 
                  id={`joint-slider-${jointName}-Y`}
                  type="range" 
                  min={limits.y[0]} 
                  max={limits.y[1]} 
                  step={jointName === 'elevation' ? 0.01 : 1} 
                  value={values[1]} 
                  onChange={(e) => handleSliderChange(1, e.target.value)}
                  onPointerDown={() => {
                    commitHistory('Slider Edit');
                    setIsPosing(true);
                  }}
                  onPointerUp={() => setIsPosing(false)}
                  disabled={isPlaying}
                  style={{ opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer' }}
                />
                <div 
                  className="slider-value" 
                  style={vertical ? {
                    left: `calc(12px + (100% - 24px) * ${(values[1] - limits.y[0]) / (limits.y[1] - limits.y[0])})`,
                    transform: 'translate(-50%, -50%) rotate(90deg)'
                  } : {
                    left: `calc(12px + (100% - 24px) * ${(values[1] - limits.y[0]) / (limits.y[1] - limits.y[0])})`
                  }}
                >
                  {jointName === 'elevation' ? values[1].toFixed(1) : Math.round(values[1])}
                </div>
              </div>
            </div>
          </div>
        )}
        {axes.includes('Z') && limits.z[0] !== limits.z[1] && (
          <div className={`flex-row ${(isSelected || isSymmetryGlow) && activeAxis === 'Z' ? 'axis-glow' : ''}`}>
            {!hideAxes && <span style={{width: 15, fontSize: '0.65rem', color: '#3b82f6', fontWeight: 'bold', lineHeight: '14px'}}>Z</span>}
            <div className="joint-slider-container">
              <input 
                type="text" 
                className="joint-number-input"
                value={localTexts[2]}
                onChange={(e) => handleTextChange(2, e.target.value)}
                onFocus={() => {
                  isFocusedRef.current[2] = true;
                  if (!isReadOnly) setSelectedJoint(jointName);
                  commitHistory('Text Edit');
                  setIsPosing(true);
                }}
                onBlur={() => {
                  handleTextBlur(2);
                  setIsPosing(false);
                }}
                onKeyDown={(e) => handleKeyDown(2, e)}
                disabled={isPlaying}
                aria-label={t('sidebar.manual_angle_input', { axis: 'Z' })}
              />
              <div className="slider-wrapper with-input">
                <input 
                  id={`joint-slider-${jointName}-Z`}
                  type="range" min={limits.z[0]} max={limits.z[1]} value={values[2]} 
                  onChange={(e) => handleSliderChange(2, e.target.value)}
                  onPointerDown={() => {
                    commitHistory('Slider Edit');
                    setIsPosing(true);
                  }}
                  onPointerUp={() => setIsPosing(false)}
                  disabled={isPlaying}
                  style={{ opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer' }}
                />
                <div className="slider-value" style={{left: `calc(12px + (100% - 24px) * ${(values[2] - limits.z[0]) / (limits.z[1] - limits.z[0])})`}}>
                  {Math.round(values[2])}
                </div>
              </div>
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

const SidebarHeader: FC<{ title: string; name: string; disabledTooltip?: string }> = ({ title, name, disabledTooltip }) => {
  const { t } = useTranslation();
  const isMinimized = useDanceStore((state: DanceState) => !!state.minimizedSidebars[name]);
  const setMinimized = useDanceStore((state: DanceState) => state.setMinimizedSidebar);
  
  return (
    <div className="sidebar-header" title={disabledTooltip}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => setMinimized(name, !isMinimized)}>
        {(name === 'left' || name === 'right') && (
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: name === 'left' ? 'rgba(239, 68, 68, 0.6)' : 'rgba(34, 197, 94, 0.6)',
            boxShadow: `0 0 10px ${name === 'left' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(34, 197, 94, 0.4)'}, inset 0 0 4px rgba(255,255,255,0.2)`,
            border: `1px solid ${name === 'left' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
            flexShrink: 0
          }} />
        )}
        <h3>{title}</h3>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <button 
          className="sidebar-toggle-btn" 
          onClick={() => setMinimized(name, !isMinimized)}
          title={isMinimized ? t('sidebar.restore') : t('sidebar.minimize')}
        >
          {isMinimized ? <Square size={8} color="#94a3b8" /> : <Minus size={8} color="#94a3b8" />}
        </button>
      </div>
    </div>
  );
};



const SidebarAccordion: FC<{ title: string; id: string; children: React.ReactNode; targetJoint?: string; highlightJoints?: string[] }> = ({ title, id, children, targetJoint, highlightJoints }) => {
  const isCollapsed = useDanceStore((state: DanceState) => !!state.collapsedSections[id]);
  const setCollapsed = useDanceStore((state: DanceState) => state.setSectionCollapsed);
  const setSelectedJoint = useDanceStore((state: DanceState) => state.setSelectedJoint);
  const isReadOnly = useDanceStore((state: DanceState) => state.isReadOnly);
  const selectedJoint = useDanceStore((state: DanceState) => state.selectedJoint);
  const isMirroring = useDanceStore((state: DanceState) => state.isMirroring);

  const joints = highlightJoints ?? (targetJoint ? [targetJoint] : []);
  const mirroredJoint = isMirroring && selectedJoint ? MIRROR_MAP[selectedJoint] : null;
  const isSelected = joints.some(j => j === selectedJoint || j === mirroredJoint);
  const isParentHighlight = !isSelected && joints.some(j =>
    (selectedJoint && JOINT_PARENT_MAP[selectedJoint] === j) ||
    (mirroredJoint && JOINT_PARENT_MAP[mirroredJoint] === j)
  );
  const titleColor = isSelected ? '#38bdf8' : isParentHighlight ? '#f87171' : (isCollapsed ? '#94a3b8' : '#e2e8f0');

  return (
    <div className={`sidebar-accordion ${isCollapsed ? 'collapsed' : ''}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', marginBottom: '1px' }}>
      <div 
        id={targetJoint ? `joint-heading-${targetJoint}` : undefined}
        className="accordion-header" 
        onClick={() => {
          setCollapsed(id, !isCollapsed);
          if (isCollapsed && targetJoint && !isReadOnly) {
            setSelectedJoint(targetJoint);
          }
        }}
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '2px 6px', 
          cursor: 'pointer',
          background: isCollapsed ? 'transparent' : 'rgba(255,255,255,0.02)',
          borderRadius: '4px',
          transition: 'all 0.2s ease'
        }}
      >
        <span style={{
          fontSize: '0.7rem',
          color: titleColor,
          fontWeight: isCollapsed && !isSelected && !isParentHighlight ? 400 : 600,
          textTransform: 'none',
          letterSpacing: 'normal',
          transition: 'all 0.2s ease'
        }}>{title}</span>
        <ChevronDown 
          size={11} 
          color={isCollapsed ? '#64748b' : '#38bdf8'} 
          style={{ 
            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', 
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isCollapsed ? 0.7 : 1
          }} 
        />
      </div>
      {!isCollapsed && (
        <div className="accordion-content" style={{ padding: '1px 0 3px 0' }}>
          {children}
        </div>
      )}
    </div>
  );
};

export const SidebarTextToPose: FC = () => {
  const { t } = useTranslation();
  const isMinimized = useDanceStore((state: DanceState) => !!state.minimizedSidebars['textToPose']);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const commitHistory = useDanceStore((state: DanceState) => state.commitHistory);
  const currentPose = useDanceStore((state: DanceState) => state.currentPose);
  const setPoseNotes = useDanceStore((state: DanceState) => state.setPoseNotes);
  const aiEnabled = useStorageStore((state) => state.preferences.showAiFeatures);

  const [activeTab, setActiveTab] = React.useState<'prompt' | 'notes'>('notes');
  const [prompt, setPrompt] = React.useState('');
  const [resultJson, setResultJson] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const initialNotesRef = React.useRef(currentPose.notes || '');

  const handleGenerate = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    try {
      const token = await executeRecaptcha('text_to_pose');
      const data = await fetchPoseDataFromText(prompt, token);
      setResultJson(JSON.stringify(data, null, 2));
      useDanceStore.getState().setToastMessage("AI pose generated successfully!");
    } catch (err: any) {
      console.error(err);
      useDanceStore.getState().setToastMessage(`Failed to generate: ${err.message || err}. Please try again.`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!resultJson.trim()) return;
    try {
      const parsed = JSON.parse(resultJson);
      commitHistory('AI Pose Generation');
      applyPoseData(parsed);
      useDanceStore.getState().setToastMessage("AI pose applied successfully!");
    } catch (err: any) {
      useDanceStore.getState().setToastMessage("Invalid JSON format", 'error');
    }
  };

  const handleCopy = () => {
    if (!resultJson.trim()) return;
    navigator.clipboard.writeText(resultJson).then(() => {
      useDanceStore.getState().setToastMessage("Copied to clipboard!");
    });
  };

  return (
    <div className={`sidebar sidebar-text-to-pose glass-panel ${isMinimized ? 'minimized' : ''}`} style={isMinimized ? { height: 'auto' } : {}}>
      <SidebarHeader title={t('sidebar.text_to_pose')} name="textToPose" />
      {!isMinimized && (
        <div className="controls-section text-to-pose-content" style={{ display: 'flex', flexDirection: 'column', gap: '3px', position: 'relative', padding: '0', flex: 1 }}>
          <div className="ai-tabs">
            <button
              id="sidebar-notes-tab"
              className={`ai-tab ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              {t('sidebar.text_to_pose_tab_notes')}
            </button>
            <button
              id="sidebar-prompt-tab"
              className={`ai-tab ${activeTab === 'prompt' ? 'active' : ''}`}
              onClick={() => setActiveTab('prompt')}
            >
              {t('sidebar.text_to_pose_tab_prompt')}
            </button>
          </div>

          {activeTab === 'prompt' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, position: 'relative', pointerEvents: aiEnabled ? 'auto' : 'none' }}>
              {loading && (
                <div className="ai-spinner-overlay">
                  <div className="ai-spinner" />
                  <span style={{ marginTop: '8px', fontSize: '0.75rem', color: '#c084fc', fontWeight: 600 }}>{t('sidebar.text_to_pose_generating')}</span>
                </div>
              )}

              {!aiEnabled && (
                <div className="ai-coming-soon-overlay">
                  <span className="ai-coming-soon-text">{t('sidebar.text_to_pose_coming_soon')}</span>
                </div>
              )}

              <textarea
                className="ai-textarea ai-textarea-expanded"
                placeholder={t('sidebar.text_to_pose_placeholder')}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={loading || isPlaying}
              />

              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  className="timeline-btn purple ai-generate-btn"
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || loading || isPlaying}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                >
                  <Bot size={13} color="#c084fc" />
                  <span style={{ fontWeight: 'bold', color: '#c084fc', fontSize: '0.55rem' }}>{t('sidebar.text_to_pose_generate')}</span>
                </button>
                <button
                  className="timeline-btn blue ai-apply-btn"
                  onClick={handleApply}
                  disabled={loading || isPlaying || !resultJson.trim()}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                >
                  <Save size={13} color="#38bdf8" />
                  <span style={{ fontWeight: 'bold', color: '#38bdf8', fontSize: '0.55rem' }}>{t('sidebar.text_to_pose_apply')}</span>
                </button>
                <button
                  className="timeline-btn ai-copy-btn"
                  onClick={handleCopy}
                  disabled={loading || isPlaying || !resultJson.trim()}
                  style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Clipboard size={13} color="#34d399" />
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <textarea
                id="sidebar-notes-textarea"
                className="ai-textarea ai-textarea-expanded"
                placeholder={t('sidebar.notes_placeholder')}
                value={currentPose.notes || ''}
                onChange={(e) => setPoseNotes(e.target.value)}
                onFocus={() => {
                  initialNotesRef.current = currentPose.notes || '';
                }}
                onBlur={() => {
                  if ((currentPose.notes || '') !== initialNotesRef.current) {
                    commitHistory('Edit Notes');
                  }
                }}
                disabled={isPlaying}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SidebarJoints: FC = () => {
  const isMinimized = useDanceStore((state: DanceState) => !!state.minimizedSidebars['joints']);
  const isMirroring = useDanceStore((state: DanceState) => state.isMirroring);
  const setIsMirroring = useDanceStore((state: DanceState) => state.setIsMirroring);
  const mirrorCurrentPose = useDanceStore((state: DanceState) => state.mirrorCurrentPose);
  const reflectJoint = useDanceStore((state: DanceState) => state.reflectJoint);
  const selectedJoint = useDanceStore((state: DanceState) => state.selectedJoint);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const showLabels = useDanceStore((state: DanceState) => state.showLabels);
  const showAxes = useDanceStore((state: DanceState) => state.showAxes);
  const { t } = useTranslation();

  return (
    <div className={`sidebar sidebar-joints glass-panel ${isMinimized ? 'minimized' : ''}`} style={isMinimized ? { height: 'auto' } : {}}>
      <SidebarHeader title={t('sidebar.joints')} name="joints" />
      {!isMinimized && (
        <div className="controls-section" style={{ display: 'flex', flexDirection: 'column', position: 'relative', paddingTop: '22px' }}>
          {/* Top Header Bar (Orbs and Utility Buttons) */}
          <div style={{ 
            position: 'absolute', top: '2px', left: 0, right: 0, height: '20px', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' 
          }}>
            {/* Top Left Red Sphere (Left) */}
            <div style={{ 
              position: 'absolute', left: '10px', width: '13px', height: '13px', borderRadius: '50%', 
              background: 'rgba(239, 68, 68, 0.6)', 
              boxShadow: '0 0 12px rgba(239, 68, 68, 0.5), inset 0 0 4px rgba(255,255,255,0.2)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '8px', fontWeight: '900',
              pointerEvents: 'auto'
            }}>L</div>
            
            {/* Top Right Green Sphere (Right) */}
            <div style={{ 
              position: 'absolute', right: '10px', width: '13px', height: '13px', borderRadius: '50%', 
              background: 'rgba(34, 197, 94, 0.6)', 
              boxShadow: '0 0 12px rgba(34, 197, 94, 0.5), inset 0 0 4px rgba(255,255,255,0.2)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '8px', fontWeight: '900',
              pointerEvents: 'auto'
            }}>R</div>
            
            {/* Central Utility Buttons */}
            <div style={{ display: 'flex', gap: '4px', pointerEvents: 'auto' }}>
              {/* Mirror Joints Button */}
              <button 
                id="tutorial-mirror-mode"
                onClick={() => setIsMirroring(!isMirroring)}
                className="icon-button mini"
                disabled={isPlaying}
                title={t('sidebar.mirror_joints_mode')}
                style={{
                  background: isMirroring ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)', 
                  width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <Link size={12} color={isMirroring ? '#38bdf8' : '#94a3b8'} />
              </button>
            
            {/* Mirror Pose (Flip) Button */}
            <button 
              id="tutorial-mirror-flip"
              onClick={() => mirrorCurrentPose()} 
              className="icon-button mini"
              disabled={isPlaying}
              title={t('sidebar.mirror_pose_flip')}
              style={{
                background: 'rgba(56, 189, 248, 0.1)', 
                width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <FlipHorizontal2 size={12} color="#38bdf8" />
            </button>
 
            {/* Reflect Joint Button */}
            <button 
              id="tutorial-reflect-joint"
              onClick={() => reflectJoint()} 
              className="icon-button mini"
              disabled={!selectedJoint || !MIRROR_MAP[selectedJoint] || isPlaying}
              title={t('sidebar.reflect_selected')}
              style={{
                background: 'rgba(56, 189, 248, 0.1)', 
                width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: (!selectedJoint || !MIRROR_MAP[selectedJoint] || isPlaying) ? 0.3 : 1, 
                cursor: (!selectedJoint || !MIRROR_MAP[selectedJoint] || isPlaying) ? 'not-allowed' : 'pointer',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <ArrowLeftRight size={12} color="#38bdf8" />
            </button>
             {/* Toggle Joint Labels Button */}
            <button 
              id="tutorial-toggle-labels"
              onClick={() => useDanceStore.getState().setShowLabels(!showLabels)} 
              className="icon-button mini"
              title={t('sidebar.toggle_joint_labels')}
              style={{
                background: showLabels ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)', 
                width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Tag size={12} color={showLabels ? '#38bdf8' : '#94a3b8'} />
            </button>
             {/* Toggle Joint Axes Button */}
            <button 
              id="tutorial-toggle-axes"
              onClick={() => useDanceStore.getState().setShowAxes(!showAxes)} 
              className="icon-button mini"
              title={t('sidebar.toggle_joint_axes')}
              style={{
                background: showAxes ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)', 
                width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Axis3d size={12} color={showAxes ? '#38bdf8' : '#94a3b8'} />
            </button>
          </div>
        </div>


          {[
            { id: 'shrug', label: t('sidebar.shrug'), left: 'leftClavicle', right: 'rightClavicle' },
            { id: 'shoulder', label: t('sidebar.shoulder'), left: 'leftShoulder', right: 'rightShoulder' },
            { id: 'elbow', label: t('sidebar.elbow'), left: 'leftElbow', right: 'rightElbow' },
            { id: 'hip', label: t('sidebar.hip'), left: 'leftHip', right: 'rightHip' },
            { id: 'palm', label: t('sidebar.palm'), left: 'leftPalm', right: 'rightPalm' },
            { id: 'knee', label: t('sidebar.knee'), left: 'leftKnee', right: 'rightKnee' },
            { id: 'foot', label: t('sidebar.foot'), left: 'leftFoot', right: 'rightFoot' },
          ].map(joint => {
            const hasY = !['leftElbow', 'leftKnee'].includes(joint.left);
            const hasZ = !['leftElbow', 'leftKnee'].includes(joint.left);

            return (
              <SidebarAccordion key={joint.id} title={joint.label} id={`joints-${joint.id}`} targetJoint={joint.left} highlightJoints={[joint.left, joint.right]}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 16px 1fr', gap: '4px', alignItems: 'center' }}>
                  <JointControl label="" jointName={joint.left} axes={['X', 'Y', 'Z']} hideAxes hideTitle />
                  
                  <div className="flex-col" style={{ gap: '1px', padding: '0', border: '1px solid transparent' }}>
                     <div style={{ height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#ef4444', fontWeight: 'bold' }}>X</div>
                     {hasY && <div style={{ height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#22c55e', fontWeight: 'bold' }}>Y</div>}
                     {hasZ && <div style={{ height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#3b82f6', fontWeight: 'bold' }}>Z</div>}
                  </div>

                  <JointControl label="" jointName={joint.right} axes={['X', 'Y', 'Z']} hideAxes hideTitle />
                </div>
              </SidebarAccordion>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const LibrarySaveButton: FC = () => {
  const currentPose = useDanceStore((state: DanceState) => state.currentPose);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const actingSource = useDanceStore((state: DanceState) => state.actingSource);
  const savePoseToLib = useStorageStore((state: StorageState) => state.savePose);
  const selectedFolderId = useStorageStore((state: StorageState) => state.selectedPoseFolderId);
  const poseFolders = useStorageStore((state: StorageState) => state.folders);
  const lastCameraState = useDanceStore((state: DanceState) => state.lastCameraState);
  const openInputDialog = useDanceStore((state: DanceState) => state.openInputDialog);
  const confirmAction = useDanceStore((state: DanceState) => state.confirmAction);
  const { t } = useTranslation();

  const handleSave = () => {
    const isLibrary = actingSource.type === 'library';
    
    const doSave = (id?: string, name?: string) => {
      const finalName = name || (isLibrary ? actingSource.name : 'Untitled');
      try {
        const savedId = savePoseToLib({
          id,
          name: finalName,
          folderId: selectedFolderId,
          pose: { ...currentPose },
          cameraState: lastCameraState || undefined
        });

        // Update editor state to link to the newly saved/updated pose
        useDanceStore.getState().setActingSource({
          type: 'library',
          id: savedId,
          name: finalName
        });
      } catch (err) {
        confirmAction({
          title: t('sidebar.save_failed'),
          message: t('sidebar.save_failed_message', { name: finalName }),
          confirmLabel: t('sidebar.retry'),
          cancelLabel: t('sidebar.cancel'),
          onConfirm: () => doSave(id, name)
        });
      }
    };

    const timestamp = new Date().toLocaleTimeString().replace(/:/g, '-');
    const defaultName = isLibrary ? actingSource.name : `Pose-${timestamp}`;

    if (isLibrary) {
      confirmAction({
        title: t('sidebar.save_pose'),
        message: t('sidebar.overwrite_message', { name: actingSource.name }),
        confirmLabel: t('sidebar.overwrite'),
        neutralLabel: t('sidebar.save_as_new'),
        cancelLabel: t('sidebar.cancel'),
        onConfirm: () => doSave(actingSource.id, actingSource.name),
        onNeutral: () => {
          const folder = poseFolders.find(f => f.id === selectedFolderId);
          openInputDialog({
            title: t('sidebar.save_as_new_pose'),
            defaultName: `${actingSource.name} (Copy)`,
            submitLabel: t('sidebar.save'),
            folderName: folder?.name,
            onSave: (name) => doSave(undefined, name)
          });
        }
      });
    } else {
      const folder = poseFolders.find(f => f.id === selectedFolderId);
      openInputDialog({
        title: t('library.save_pose_to_library'),
        defaultName,
        submitLabel: t('sidebar.save'),
        folderName: folder?.name,
        onSave: (name) => doSave(undefined, name)
      });
    }
  };

  return (
    <div id="pose-save-to-library-container" className="timeline-btn blue" style={{
      display: 'flex',
      alignItems: 'center',
      padding: '0 4px 0 4px',
      gap: '0',
      height: '26px',
      minWidth: 'auto',
      width: '136px',
      margin: '4px',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      opacity: isPlaying ? 0.3 : 1, 
      pointerEvents: 'auto'
    }}>
      <button 
        onClick={handleSave}
        disabled={isPlaying}
        className="icon-button mini"
        title={t('sidebar.save_pose_to_library_tooltip')}
        style={{
          background: 'transparent',
          border: 'none',
          height: '100%',
          padding: '0 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          color: 'inherit',
          cursor: isPlaying ? 'not-allowed' : 'pointer',
          flex: 1
        }}
      >
        <Save size={14} />
        <span style={{ fontSize: '0.7rem', fontWeight: 'bold', marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {t('sidebar.save_pose_to_library')}
          <ArrowUp size={14} />
        </span>
      </button>
      
      {/* Spacer to match the checkbox area of the other button */}
      <div style={{ width: '1px', height: '14px', background: 'currentColor', opacity: 0.2 }} />
      <div style={{ width: '22px' }} /> 
    </div>
  );
};

export const PoseFolderStatus: FC = () => {
  const selectedFolderId = useStorageStore((state: StorageState) => state.selectedPoseFolderId);
  const poseFolders = useStorageStore((state: StorageState) => state.folders);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const { t } = useTranslation();

  const selectedFolder = selectedFolderId ? poseFolders.find(f => f.id === selectedFolderId) : null;
  const folderName = selectedFolderId === null ? t('folderTree.root', { defaultValue: 'Root' }) : (selectedFolder?.name ?? t('folderTree.root', { defaultValue: 'Root' }));

  const getFolderPath = (folderId: string | null): string => {
    if (folderId === null) return t('folderTree.root', { defaultValue: 'Root' });
    const path: string[] = [];
    let curr: string | null = folderId;
    while (curr) {
      const f = poseFolders.find(x => x.id === curr);
      if (!f) break;
      path.unshift(f.name);
      curr = f.parentId;
    }
    return path.join(' > ');
  };
  const folderPath = getFolderPath(selectedFolderId);

  return (
    <div
      id="pose-folder-status-container"
      title={folderPath}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '0 8px',
        cursor: 'default',
        width: '136px',
        height: '20px',
        opacity: isPlaying ? 0.3 : 1,
        justifyContent: 'flex-start',
        pointerEvents: 'auto',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div style={{ color: '#38bdf8', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <FolderOpen size={14} />
      </div>
      <div className="folder-name" style={{
        fontSize: '0.7rem',
        fontWeight: 'bold',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100px',
        marginLeft: '4px'
      }}>
        {folderName}
      </div>
    </div>
  );
};

export const PoseSaveToFrameButton: FC = () => {
  const addKeyframe = useDanceStore((state: DanceState) => state.addKeyframe);
  const hasUnsavedChanges = useDanceStore((state: DanceState) => state.hasUnsavedChanges);
  const autoSaveEnabled = useDanceStore((state: DanceState) => state.autoSaveEnabled);
  const setAutoSaveEnabled = useDanceStore((state: DanceState) => state.setAutoSaveEnabled);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const actingSource = useDanceStore((state: DanceState) => state.actingSource);
  const { t } = useTranslation();

  const keyframes = useDanceStore((state: DanceState) => state.keyframes);
  const currentTime = useDanceStore((state: DanceState) => state.currentTime);
  const isKeyframeAtCurrent = keyframes.some(kf => Math.abs(kf.time - currentTime) < 0.05);

  const getButtonLabel = () => {
    if (actingSource.type === 'library') return t('sidebar.apply_pose_to_frame');
    if (isKeyframeAtCurrent) return t('sidebar.update_keyframe');
    return t('sidebar.save_pose_to_frame');
  };

  const getButtonTitle = () => {
    if (actingSource.type === 'library') return t('sidebar.apply_pose_to_frame_tooltip', { name: actingSource.name });
    if (isKeyframeAtCurrent) return t('sidebar.update_keyframe_tooltip', { time: currentTime.toFixed(2) });
    return t('sidebar.save_pose_to_frame_tooltip', { time: currentTime.toFixed(2) });
  };

  return (
    <div id="pose-save-to-frame-container" className={`timeline-btn blue ${hasUnsavedChanges ? 'unsaved' : ''}`} style={{
      display: 'flex',
      alignItems: 'center',
      padding: '0 4px 0 4px',
      gap: '0',
      height: '26px',
      minWidth: 'auto',
      width: '136px',
      margin: '4px',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      boxShadow: hasUnsavedChanges
        ? '0 8px 16px -2px rgba(0, 0, 0, 0.4), 0 0 7.5px rgba(253, 224, 71, 1), 0 0 12.5px rgba(253, 224, 71, 0.5)'
        : '0 8px 16px -2px rgba(0, 0, 0, 0.4)',
      opacity: (isPlaying || (actingSource.type === 'none' && !hasUnsavedChanges)) ? 0.3 : 1,
      pointerEvents: 'auto',
      borderRadius: '6px', 
      background: 'rgba(30, 41, 59, 0.8)',
      borderColor: 'rgba(56, 189, 248, 0.4)'
    }}>
      <button 
        id="pose-save-to-frame"
        onClick={() => addKeyframe()} 
        disabled={isPlaying || (actingSource.type === 'none' && !hasUnsavedChanges)} 
        title={getButtonTitle()}
        style={{
          background: 'transparent',
          border: 'none',
          height: '100%',
          padding: '0 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          color: 'inherit',
          cursor: (isPlaying || (actingSource.type === 'none' && !hasUnsavedChanges)) ? 'not-allowed' : 'pointer',
          flex: 1
        }}
      >
        <Save size={14}/>
        <span style={{ fontSize: '0.7rem', fontWeight: 'bold', marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {getButtonLabel()}
          <ArrowDown size={14} />
        </span>
      </button>

      <div style={{ width: '1px', height: '14px', background: 'currentColor', opacity: 0.2 }} />

      <label 
        title={t('sidebar.auto_save_tooltip')}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: isPlaying ? 'not-allowed' : 'pointer',
          height: '100%',
          paddingLeft: '8px',
          paddingRight: '2px',
          position: 'relative'
        }}
      >
        <input 
          id="sidebar-auto-save-checkbox"
          type="checkbox" 
          checked={autoSaveEnabled}
          onChange={(e) => setAutoSaveEnabled(e.target.checked)}
          disabled={isPlaying}
          style={{
            appearance: 'none',
            width: '12px',
            height: '12px',
            border: `1px solid ${hasUnsavedChanges ? '#fde047' : 'currentColor'}`,
            borderRadius: '2px',
            background: autoSaveEnabled ? (hasUnsavedChanges ? 'rgba(253, 224, 71, 0.1)' : 'rgba(56, 189, 248, 0.1)') : 'transparent',
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            margin: 0
          }}
        />
        {autoSaveEnabled && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '14px', /* Adjusted for paddingLeft */
            width: '6px',
            height: '6px',
            background: hasUnsavedChanges ? '#fde047' : '#38bdf8',
            borderRadius: '1px',
            pointerEvents: 'none',
            transform: 'translate(-50%, -50%)',
            boxShadow: hasUnsavedChanges ? '0 0 5px rgba(253, 224, 71, 0.5)' : 'none'
          }} />
        )}
      </label>
    </div>
  );
};

export const PoseSourceStatus: FC = () => {
  const actingSource = useDanceStore((state: DanceState) => state.actingSource);
  const hasUnsavedChanges = useDanceStore((state: DanceState) => state.hasUnsavedChanges);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const { t } = useTranslation();

  const getSourceLabel = () => {
    switch (actingSource.type) {
      case 'timeline': return t('workspace.frame', { time: actingSource.time.toFixed(2) });
      case 'library': return t('workspace.library', { name: actingSource.name });
      default: return t('workspace.manual');
    }
  };

  const statusColor = hasUnsavedChanges ? '#fbbf24' : '#38bdf8';
  const glowColor = hasUnsavedChanges ? 'rgba(251, 191, 36, 0.2)' : 'rgba(56, 189, 248, 0.2)';

  return (
    <div id="pose-source-status-container" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '0 8px',
      height: '20px',
      width: '136px',
      opacity: isPlaying ? 0.3 : 1,
      pointerEvents: 'auto',
      cursor: 'default',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      <div style={{ color: statusColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <PersonStanding size={14} />
      </div>
      <div style={{
        color: '#f8fafc',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginLeft: '4px',
        flex: 1,
        textAlign: 'left'
      }} title={getSourceLabel()}>
        {getSourceLabel()}
      </div>
      {hasUnsavedChanges && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          color: '#fbbf24',
          flexShrink: 0,
          marginLeft: '4px',
          animation: 'workspace-status-pulse 2s infinite'
        }} title={t('workspace.modified')}>
          <AlertCircle size={10} />
        </div>
      )}
    </div>
  );
};

// Hip height in world space (torsoBaseY * mannequin scale 0.5)
const HIP_HEIGHT_WORLD = 7.77 * 0.5;
const RAD2DEG = 180 / Math.PI;
const MAX_THRUST = 1.44;
const MAX_LATERAL_DEG = 25;
const MAX_SQUAT_A = 180;
const LEG_RATIO = 3.44 / 4.22;

export const SidebarCore: FC = () => {
  const isMinimized = useDanceStore((state: DanceState) => !!state.minimizedSidebars['core']);
  const { t } = useTranslation();
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const isReadOnly = useDanceStore((state: DanceState) => state.isReadOnly);
  const pelvisJoint = useDanceStore(useShallow((state: DanceState) => state.currentPose.pelvis as [number, number, number]));
  const stomach = useDanceStore(useShallow((state: DanceState) => state.currentPose.stomach as [number, number, number]));
  const torsoJoint = useDanceStore(useShallow((state: DanceState) => state.currentPose.torso as [number, number, number]));
  const elevation = useDanceStore(useShallow((state: DanceState) => state.currentPose.elevation as [number, number, number]));
  const leftHip = useDanceStore(useShallow((state: DanceState) => state.currentPose.leftHip as [number, number, number]));
  const rightHip = useDanceStore(useShallow((state: DanceState) => state.currentPose.rightHip as [number, number, number]));
  const leftKnee = useDanceStore(useShallow((state: DanceState) => state.currentPose.leftKnee as [number, number, number]));
  const rightKnee = useDanceStore(useShallow((state: DanceState) => state.currentPose.rightKnee as [number, number, number]));
  const leftFootCore = useDanceStore(useShallow((state: DanceState) => state.currentPose.leftFoot as [number, number, number]));
  const rightFootCore = useDanceStore(useShallow((state: DanceState) => state.currentPose.rightFoot as [number, number, number]));
  const chest = useDanceStore(useShallow((state: DanceState) => state.currentPose.chest as [number, number, number]));
  const neck = useDanceStore(useShallow((state: DanceState) => state.currentPose.neck as [number, number, number]));
  const setJointRotation = useDanceStore((state: DanceState) => state.setJointRotation);
  const commitHistory = useDanceStore((state: DanceState) => state.commitHistory);
  const setIsPosing = useDanceStore((state: DanceState) => state.setIsPosing);

  const disabled = isPlaying || isReadOnly;

  const thrustVal = Math.round(Math.max(-180, Math.min(180, ((elevation?.[2] ?? 0) / MAX_THRUST) * 180)));
  const MAX_PELVIC_TILT_CORE = 30;
  const lateralVal = Math.round(Math.max(-100, Math.min(100, ((pelvisJoint?.[2] ?? 0) / MAX_PELVIC_TILT_CORE) * 100)));
  const verticalVal = Math.round(Math.max(0, Math.min(100, (-(leftHip?.[0] ?? 0) / MAX_SQUAT_A) * 100)));

  const thrustRef = useRef(thrustVal);
  const verticalRef = useRef(verticalVal);

  useEffect(() => {
    thrustRef.current = thrustVal;
    if (Math.abs(thrustVal) < 1) {
      verticalRef.current = verticalVal;
    }
  }, [thrustVal, verticalVal]);

  const [localThrustText, setLocalThrustText] = React.useState('');
  const [localLateralText, setLocalLateralText] = React.useState('');
  const [localVerticalText, setLocalVerticalText] = React.useState('');

  const isThrustFocused = useRef(false);
  const isLateralFocused = useRef(false);
  const isVerticalFocused = useRef(false);

  useEffect(() => {
    if (!isThrustFocused.current) {
      setLocalThrustText(String(thrustVal));
    }
  }, [thrustVal]);

  useEffect(() => {
    if (!isLateralFocused.current) {
      setLocalLateralText(String(lateralVal));
    }
  }, [lateralVal]);

  useEffect(() => {
    if (!isVerticalFocused.current) {
      setLocalVerticalText(String(verticalVal));
    }
  }, [verticalVal]);

  const handleThrustTextChange = (text: string) => {
    setLocalThrustText(text);
    const num = Number(text);
    if (!isNaN(num) && text.trim() !== '' && text !== '-') {
      const clamped = clamp(num, -180, 180);
      applyThrust(clamped);
    }
  };

  const handleThrustTextBlur = () => {
    isThrustFocused.current = false;
    const num = Number(localThrustText);
    let finalVal = thrustVal;
    if (!isNaN(num) && localThrustText.trim() !== '') {
      finalVal = clamp(num, -180, 180);
    }
    applyThrust(finalVal);
    setLocalThrustText(String(finalVal));
  };

  const handleLateralTextChange = (text: string) => {
    setLocalLateralText(text);
    const num = Number(text);
    if (!isNaN(num) && text.trim() !== '' && text !== '-') {
      const clamped = clamp(num, -100, 100);
      applyLateral(clamped);
    }
  };

  const handleLateralTextBlur = () => {
    isLateralFocused.current = false;
    const num = Number(localLateralText);
    let finalVal = lateralVal;
    if (!isNaN(num) && localLateralText.trim() !== '') {
      finalVal = clamp(num, -100, 100);
    }
    applyLateral(finalVal);
    setLocalLateralText(String(finalVal));
  };

  const handleVerticalTextChange = (text: string) => {
    setLocalVerticalText(text);
    const num = Number(text);
    if (!isNaN(num) && text.trim() !== '') {
      const clamped = clamp(num, 0, 100);
      applyVertical(clamped);
    }
  };

  const handleVerticalTextBlur = () => {
    isVerticalFocused.current = false;
    const num = Number(localVerticalText);
    let finalVal = verticalVal;
    if (!isNaN(num) && localVerticalText.trim() !== '') {
      finalVal = clamp(num, 0, 100);
    }
    applyVertical(finalVal);
    setLocalVerticalText(String(finalVal));
  };

  const handleTorsoKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const computeKneeAndHip = (thrustV: number, vertV: number) => {
    const dZ = (thrustV / 180) * MAX_THRUST;
    const hipXThrust = Math.atan2(dZ, HIP_HEIGHT_WORLD) * RAD2DEG;
    const squat_a = (vertV / 100) * MAX_SQUAT_A;
    const squat_aRad = squat_a * (Math.PI / 180);
    const vertKneeBeta = squat_a + Math.asin(Math.min(LEG_RATIO * Math.sin(squat_aRad), 1)) * RAD2DEG;
    const thrustKneeComp = thrustV < 0 ? -hipXThrust : 0;
    return { hipX: hipXThrust - squat_a, kneeX: vertKneeBeta + thrustKneeComp };
  };

  const applyVertical = (val: number) => {
    verticalRef.current = val;
    const { hipX, kneeX } = computeKneeAndHip(thrustRef.current, val);
    setJointRotation('leftHip', hipX, leftHip[1], leftHip[2]);
    setJointRotation('rightHip', hipX, rightHip[1], rightHip[2]);
    setJointRotation('leftKnee', kneeX, leftKnee[1], leftKnee[2]);
    setJointRotation('rightKnee', kneeX, rightKnee[1], rightKnee[2]);
    setJointRotation('elevation', elevation[0], -99, elevation[2]);
  };

  const applyThrust = (val: number) => {
    thrustRef.current = val;
    const dZ = (val / 180) * MAX_THRUST;
    const baseComp = -Math.atan2(dZ, HIP_HEIGHT_WORLD * 0.4) * RAD2DEG;
    const torsoXComp = baseComp * 0.6;
    const stomachXComp = -baseComp * 0.6;
    const { hipX, kneeX } = computeKneeAndHip(val, verticalRef.current);
    setJointRotation('leftHip', hipX, leftHip[1], leftHip[2]);
    setJointRotation('rightHip', hipX, rightHip[1], rightHip[2]);
    setJointRotation('leftKnee', kneeX, leftKnee[1], leftKnee[2]);
    setJointRotation('rightKnee', kneeX, rightKnee[1], rightKnee[2]);
    setJointRotation('torso', torsoXComp, torsoJoint[1], torsoJoint[2]);
    setJointRotation('stomach', stomachXComp, stomach[1], stomach[2]);
    setJointRotation('elevation', elevation?.[0] ?? 0, -99, dZ);
  };

  const applyLateral = (val: number) => {
    // Mirroring must be suppressed: each setJointRotation would otherwise fight its own mirror call.
    const store = useDanceStore.getState();
    const wasMirroring = store.isMirroring;
    if (wasMirroring) store.setIsMirroring(false);

    const tilt = (val / 100) * MAX_PELVIC_TILT_CORE;
    const absTilt = Math.abs(tilt);
    const tiltRad = tilt * (Math.PI / 180);
    const drop = 0.78 * Math.abs(Math.sin(tiltRad));
    const sinA = Math.min(drop / (4.22 + 3.44), 1);
    const aDeg = Math.asin(sinA) * RAD2DEG;
    const kneeBend = aDeg + Math.asin(Math.min(LEG_RATIO * sinA, 1)) * RAD2DEG + absTilt * 0.5;
    const leftFootX  =  tilt * 0.7;
    const rightFootX = -tilt * 0.7;
    const leftFootZ  =  tilt * 0.7;
    const rightFootZ = -tilt * 0.7;
    const torsoZ   = -tilt * 0.5;
    const stomachZ = -tilt * 0.3;
    const chestZ   = -tilt * 0.15;
    const neckZ    = -tilt * 0.05;
    setJointRotation('pelvis', 0, 0, tilt);
    setJointRotation('leftHip', leftHip[0], leftHip[1], -tilt);
    setJointRotation('rightHip', rightHip[0], rightHip[1], -tilt);
    setJointRotation('leftKnee', tilt < 0 ? kneeBend : 0, leftKnee[1], leftKnee[2]);
    setJointRotation('rightKnee', tilt > 0 ? kneeBend : 0, rightKnee[1], rightKnee[2]);
    setJointRotation('leftFoot', leftFootX, leftFootCore?.[1] ?? 0, leftFootZ);
    setJointRotation('rightFoot', rightFootX, rightFootCore?.[1] ?? 0, rightFootZ);
    setJointRotation('torso', torsoJoint[0], torsoJoint[1], torsoZ);
    setJointRotation('stomach', stomach[0], stomach[1], stomachZ);
    setJointRotation('chest', chest[0], chest[1], chestZ);
    setJointRotation('neck', neck[0], neck[1], neckZ);
    setJointRotation('elevation', elevation?.[0] ?? 0, -99, elevation?.[2] ?? 0);
    if (wasMirroring) store.setIsMirroring(true);
  };

  const sliderPos = (val: number, min: number, max: number) => `calc(12px + (100% - 24px) * ${(val - min) / (max - min)})`;

  return (
    <div className={`sidebar sidebar-core glass-panel ${isMinimized ? 'minimized' : ''}`} style={isMinimized ? { height: 'auto' } : { paddingBottom: '0' }}>
      <SidebarHeader title={t('sidebar.core')} name="core" />
      {!isMinimized && (
        <div className="controls-section" style={{ display: 'flex', flexDirection: 'column', marginBottom: '0' }}>
          <SidebarAccordion title={t('sidebar.head')} id="core-head" targetJoint="head">
            <JointControl label="" jointName="head" axes={['X', 'Y', 'Z']} hideTitle />
          </SidebarAccordion>
          <SidebarAccordion title={t('sidebar.neck')} id="core-neck" targetJoint="neck">
            <JointControl label="" jointName="neck" axes={['X', 'Y', 'Z']} hideTitle />
          </SidebarAccordion>
          <SidebarAccordion title={t('sidebar.chest')} id="core-chest" targetJoint="chest">
            <JointControl label="" jointName="chest" axes={['X', 'Y', 'Z']} hideTitle />
          </SidebarAccordion>
          <SidebarAccordion title={t('sidebar.stomach')} id="core-stomach" targetJoint="stomach">
            <JointControl label="" jointName="stomach" axes={['X', 'Y', 'Z']} hideTitle />
          </SidebarAccordion>
          <SidebarAccordion title={t('sidebar.torso')} id="core-torso" targetJoint="torso">
            <JointControl label="" jointName="torso" axes={['X', 'Y', 'Z']} hideTitle>
              <div className="flex-row">
                <span style={{width: 15, fontSize: '0.65rem', color: '#f59e0b', fontWeight: 'bold', lineHeight: '14px'}}>T</span>
                <div className="joint-slider-container">
                  <input 
                    type="text" 
                    className="joint-number-input"
                    value={localThrustText}
                    onChange={(e) => handleThrustTextChange(e.target.value)}
                    onFocus={() => {
                      isThrustFocused.current = true;
                      commitHistory('Hip Thrust');
                      setIsPosing(true);
                    }}
                    onBlur={() => {
                      handleThrustTextBlur();
                      setIsPosing(false);
                    }}
                    onKeyDown={handleTorsoKeyDown}
                    disabled={disabled}
                    aria-label={t('sidebar.manual_angle_input', { axis: 'T' })}
                  />
                  <div className="slider-wrapper with-input">
                    <input 
                      id="joint-slider-torso-T"
                      type="range" min={-180} max={180} value={thrustVal}
                      onChange={(e) => applyThrust(Number(e.target.value))}
                      onPointerDown={() => { commitHistory('Hip Thrust'); setIsPosing(true); }}
                      onPointerUp={() => setIsPosing(false)}
                      disabled={disabled}
                      style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                    <div className="slider-value" style={{ left: sliderPos(thrustVal, -180, 180) }}>{thrustVal}</div>
                  </div>
                </div>
              </div>
              <div className="flex-row">
                <span style={{width: 15, fontSize: '0.65rem', color: '#a78bfa', fontWeight: 'bold', lineHeight: '14px'}}>L</span>
                <div className="joint-slider-container">
                  <input 
                    type="text" 
                    className="joint-number-input"
                    value={localLateralText}
                    onChange={(e) => handleLateralTextChange(e.target.value)}
                    onFocus={() => {
                      isLateralFocused.current = true;
                      commitHistory('Hip Lateral');
                      setIsPosing(true);
                    }}
                    onBlur={() => {
                      handleLateralTextBlur();
                      setIsPosing(false);
                    }}
                    onKeyDown={handleTorsoKeyDown}
                    disabled={disabled}
                    aria-label={t('sidebar.manual_angle_input', { axis: 'L' })}
                  />
                  <div className="slider-wrapper with-input">
                    <input
                      id="joint-slider-torso-L"
                      type="range" min={-100} max={100} value={lateralVal}
                      onChange={(e) => applyLateral(Number(e.target.value))}
                      onPointerDown={() => { commitHistory('Hip Lateral'); setIsPosing(true); }}
                      onPointerUp={() => setIsPosing(false)}
                      disabled={disabled}
                      style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                    <div className="slider-value" style={{ left: sliderPos(lateralVal, -100, 100) }}>{lateralVal}</div>
                  </div>
                </div>
              </div>
              <div className="flex-row">
                <span style={{width: 15, fontSize: '0.65rem', color: '#38bdf8', fontWeight: 'bold', lineHeight: '14px'}}>V</span>
                <div className="joint-slider-container">
                  <input 
                    type="text" 
                    className="joint-number-input"
                    value={localVerticalText}
                    onChange={(e) => handleVerticalTextChange(e.target.value)}
                    onFocus={() => {
                      isVerticalFocused.current = true;
                      commitHistory('Hip Vertical');
                      setIsPosing(true);
                    }}
                    onBlur={() => {
                      handleVerticalTextBlur();
                      setIsPosing(false);
                    }}
                    onKeyDown={handleTorsoKeyDown}
                    disabled={disabled}
                    aria-label={t('sidebar.manual_angle_input', { axis: 'V' })}
                  />
                  <div className="slider-wrapper with-input">
                    <input 
                      id="joint-slider-torso-V"
                      type="range" min={0} max={100} value={verticalVal}
                      onChange={(e) => applyVertical(Number(e.target.value))}
                      onPointerDown={() => { commitHistory('Hip Vertical'); setIsPosing(true); }}
                      onPointerUp={() => setIsPosing(false)}
                      disabled={disabled}
                      style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                    <div className="slider-value" style={{ left: `calc(12px + (100% - 24px) * ${verticalVal / 100})` }}>{verticalVal}</div>
                  </div>
                </div>
              </div>
            </JointControl>
          </SidebarAccordion>
        </div>
      )}
    </div>
  );
};

export const SidebarAttitude: FC = () => {
  const { t } = useTranslation();
  const isMinimized = useDanceStore((state: DanceState) => !!state.minimizedSidebars['attitude']);
  const currentPose = useDanceStore((state: DanceState) => state.currentPose);
  const setRotation = useDanceStore((state: DanceState) => state.setRotation);
  const resetOrientation = useDanceStore((state: DanceState) => state.resetOrientation);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const commitHistory = useDanceStore((state: DanceState) => state.commitHistory);
  const setJointRotation = useDanceStore((state: DanceState) => state.setJointRotation);
  const groundDancer = useDanceStore((state: DanceState) => state.groundDancer);

  const orientation = currentPose.orientation || [0, 0, 0];
  const isOriented = orientation.some(v => Math.abs(v) > 0.001);
  const orientationY = orientation[1];
  const groundedElev = useDanceStore(state => state.groundedElevation);
  const currentElevY = (currentPose.elevation || [0,0,0])[1];
  const isGrounded = Math.abs(currentElevY - groundedElev) < 0.01;
  const sliderMinElev = Math.round(groundedElev * 100);
  const sliderMaxElev = Math.max(sliderMinElev + 300, Math.round(currentElevY * 100) + 50);
  const addFootMarker = useDanceStore((state: DanceState) => state.addFootMarker);
  const footMarkers = useDanceStore((state: DanceState) => state.footMarkers);
  const addLocationMarker = useDanceStore((state: DanceState) => state.addLocationMarker);
  const clearAllMarkers = useDanceStore((state: DanceState) => state.clearAllMarkers);
  const locationMarkers = useDanceStore((state: DanceState) => state.locationMarkers);

  const [localOrientText, setLocalOrientText] = React.useState('');
  const [localElevText, setLocalElevText] = React.useState('');

  const isOrientFocused = useRef(false);
  const isElevFocused = useRef(false);

  useEffect(() => {
    if (!isOrientFocused.current) {
      setLocalOrientText(String(Math.round(orientationY)));
    }
  }, [orientationY]);

  useEffect(() => {
    if (!isElevFocused.current) {
      setLocalElevText(String(Math.round(currentElevY * 100)));
    }
  }, [currentElevY]);

  const handleOrientTextChange = (text: string) => {
    setLocalOrientText(text);
    const num = Number(text);
    if (!isNaN(num) && text.trim() !== '' && text !== '-') {
      const clamped = clamp(num, -180, 180);
      setRotation(clamped);
    }
  };

  const handleOrientTextBlur = () => {
    isOrientFocused.current = false;
    const num = Number(localOrientText);
    let finalVal = orientationY;
    if (!isNaN(num) && localOrientText.trim() !== '') {
      finalVal = clamp(num, -180, 180);
    }
    setRotation(finalVal);
    setLocalOrientText(String(Math.round(finalVal)));
  };

  const handleElevTextChange = (text: string) => {
    setLocalElevText(text);
    const num = Number(text);
    if (!isNaN(num) && text.trim() !== '' && text !== '-') {
      const minVal = Math.round(groundedElev * 100);
      const clamped = Math.max(minVal, num);
      setJointRotation('elevation', 0, clamped / 100, 0);
    }
  };

  const handleElevTextBlur = () => {
    isElevFocused.current = false;
    const num = Number(localElevText);
    const minVal = Math.round(groundedElev * 100);
    let finalVal = Math.round(currentElevY * 100);
    if (!isNaN(num) && localElevText.trim() !== '') {
      finalVal = Math.max(minVal, num);
    }
    setJointRotation('elevation', 0, finalVal / 100, 0);
    setLocalElevText(String(finalVal));
  };

  const handleAttitudeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className={`sidebar sidebar-attitude glass-panel ${isMinimized ? 'minimized' : ''}`} style={isMinimized ? { height: 'auto' } : { paddingBottom: '3px' }}>
      <SidebarHeader title={t('sidebar.attitude')} name="attitude" />
      {!isMinimized && (
        <div className="controls-section" style={{ display: 'flex', flexDirection: 'column' }}>

          <div id="tutorial-orientation-control" className="flex-row" style={{ marginTop: '2px', padding: '0 3px' }}>
            <span style={{ width: 15, fontSize: '0.65rem', color: '#a78bfa', fontWeight: 'bold', lineHeight: '14px' }} title={t('sidebar.orientation')}>O</span>
            <div className="joint-slider-container" style={{ flex: 1, minWidth: 0 }}>
              <input 
                type="text" 
                className="joint-number-input"
                style={{ flexShrink: 0 }}
                value={localOrientText}
                onChange={(e) => handleOrientTextChange(e.target.value)}
                onFocus={() => {
                  isOrientFocused.current = true;
                  commitHistory('Slider Edit');
                  useDanceStore.getState().setIsPosing(true);
                }}
                onBlur={() => {
                  handleOrientTextBlur();
                  useDanceStore.getState().setIsPosing(false);
                }}
                onKeyDown={handleAttitudeKeyDown}
                disabled={isPlaying}
                aria-label={t('sidebar.manual_angle_input', { axis: 'O' })}
              />
              <div className="slider-wrapper with-input" style={{ flex: 1, width: 'auto', minWidth: 0 }}>
                <input 
                  type="range" min="-180" max="180" 
                  value={Math.round(orientationY)} 
                  onChange={(e) => setRotation(Number(e.target.value))}
                  onPointerDown={() => {
                    commitHistory('Slider Edit');
                    useDanceStore.getState().setIsPosing(true);
                  }}
                  onPointerUp={() => useDanceStore.getState().setIsPosing(false)}
                  disabled={isPlaying}
                  style={{ opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer' }}
                />
                <div className="slider-value" style={{ left: `calc(12px + (100% - 24px) * ${(Math.round(orientationY) + 180) / 360})` }}>
                  {Math.round(orientationY)}
                </div>
              </div>
            </div>
            <button 
              id="tutorial-reset-orientation"
              className="icon-button mini"
              onClick={() => resetOrientation()}
              disabled={isPlaying}
              title={t('sidebar.reset_orientation')}
              style={{
                background: isOriented ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                width: '20px', height: '20px', flexShrink: 0, borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <RotateCcw size={11} color={isOriented ? '#38bdf8' : '#94a3b8'} />
            </button>
          </div>

          <div id="tutorial-height-control" className="flex-row" style={{ marginTop: '2px', padding: '0 3px' }}>
            <span style={{ width: 15, fontSize: '0.65rem', color: '#38bdf8', fontWeight: 'bold', lineHeight: '14px' }} title={t('sidebar.elevation')}>E</span>
            <div className="joint-slider-container" style={{ flex: 1, minWidth: 0 }}>
              <input 
                type="text" 
                className="joint-number-input"
                style={{ flexShrink: 0 }}
                value={localElevText}
                onChange={(e) => handleElevTextChange(e.target.value)}
                onFocus={() => {
                  isElevFocused.current = true;
                  commitHistory('Slider Edit');
                  useDanceStore.getState().setIsPosing(true);
                }}
                onBlur={() => {
                  handleElevTextBlur();
                  useDanceStore.getState().setIsPosing(false);
                }}
                onKeyDown={handleAttitudeKeyDown}
                disabled={isPlaying}
                aria-label={t('sidebar.manual_angle_input', { axis: 'E' })}
              />
              <div className="slider-wrapper with-input" style={{ flex: 1, width: 'auto', minWidth: 0 }}>
                <input
                  type="range" min={sliderMinElev} max={sliderMaxElev} step={1}
                  value={Math.round(currentElevY * 100)}
                  onChange={(e) => setJointRotation('elevation', 0, Number(e.target.value) / 100, 0)}
                  onPointerDown={() => {
                    commitHistory('Slider Edit');
                    useDanceStore.getState().setIsPosing(true);
                  }}
                  onPointerUp={() => useDanceStore.getState().setIsPosing(false)}
                  disabled={isPlaying}
                  style={{ opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer' }}
                />
                <div className="slider-value" style={{ left: `calc(12px + (100% - 24px) * ${(Math.round(currentElevY * 100) - sliderMinElev) / (sliderMaxElev - sliderMinElev)})` }}>
                  {Math.round(currentElevY * 100)}
                </div>
              </div>
            </div>
            <button 
              id="tutorial-ground-button"
              className={`icon-button mini ${!isGrounded ? 'ground-highlight' : ''}`}
              onClick={() => groundDancer()}
              disabled={isPlaying}
              title={t('sidebar.snap_to_ground')}
              style={{
                background: isGrounded ? 'rgba(255, 255, 255, 0.05)' : 'rgba(56, 189, 248, 0.2)',
                width: '20px', height: '20px', flexShrink: 0, borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPlaying ? 0.5 : 1, cursor: isPlaying ? 'not-allowed' : 'pointer',
                border: isGrounded ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(56, 189, 248, 0.4)'
              }}
            >
              <ChevronDown size={12} color={isGrounded ? '#94a3b8' : '#38bdf8'} />
            </button>
          </div>

          <div id="tutorial-markers-control" className="flex-row" style={{ marginTop: '2px', padding: '0 3px' }}>
            <span style={{ width: 15, fontSize: '0.65rem', color: '#10b981', fontWeight: 'bold', lineHeight: '14px' }} title={t('sidebar.markers')}>M</span>
            <div className="flex-row" style={{ gap: '2px' }}>
              <button
                id="tutorial-mark-foot"
                onClick={() => addFootMarker()}
                disabled={!isGrounded || isPlaying}
                className="icon-button mini"
                title={t('sidebar.mark_feet_tooltip')}
                style={{
                  background: isGrounded ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: (!isGrounded || isPlaying) ? 0.3 : 1, 
                  cursor: (!isGrounded || isPlaying) ? 'not-allowed' : 'pointer',
                  border: isGrounded ? '1px solid rgba(56, 189, 248, 0.4)' : '1px solid transparent'
                }}
              >
                <Footprints size={11} color={isGrounded ? '#38bdf8' : '#94a3b8'} />
              </button>
              
              <button
                id="tutorial-mark-location"
                onClick={() => addLocationMarker()}
                disabled={isPlaying}
                className="icon-button mini"
                title={t('sidebar.mark_location_tooltip')}
                style={{
                  background: 'rgba(56, 189, 248, 0.2)',
                  width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: isPlaying ? 0.3 : 1, 
                  cursor: isPlaying ? 'not-allowed' : 'pointer',
                  border: '1px solid rgba(56, 189, 248, 0.4)'
                }}
              >
                <MapPin size={11} color="#38bdf8" />
              </button>
            </div>
 
            {/* Spacer to push Delete All Markers button to the right */}
            <div style={{ flex: 1 }} />
 
            <button
              id="tutorial-clear-all-markers"
              onClick={() => clearAllMarkers()}
              disabled={(footMarkers.length === 0 && locationMarkers.length === 0) || isPlaying}
              className="icon-button mini"
              title={t('sidebar.clear_markers_tooltip')}
              style={{
                background: (footMarkers.length > 0 || locationMarkers.length > 0) ? 'rgba(244, 63, 94, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                width: '20px', height: '20px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: ((footMarkers.length === 0 && locationMarkers.length === 0) || isPlaying) ? 0.3 : 1, 
                cursor: ((footMarkers.length === 0 && locationMarkers.length === 0) || isPlaying) ? 'not-allowed' : 'pointer',
                border: (footMarkers.length > 0 || locationMarkers.length > 0) ? '1px solid rgba(244, 63, 94, 0.3)' : '1px solid transparent'
              }}
            >
              <Trash2 size={11} color={(footMarkers.length > 0 || locationMarkers.length > 0) ? '#fb7185' : '#94a3b8'} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const SidebarHip: FC = () => {
  const { t } = useTranslation();
  const isMinimized = useDanceStore((state: DanceState) => !!state.minimizedSidebars['hip']);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const isReadOnly = useDanceStore((state: DanceState) => state.isReadOnly);
  const pelvis = useDanceStore(useShallow((state: DanceState) => state.currentPose.pelvis as [number, number, number]));
  const torso = useDanceStore(useShallow((state: DanceState) => state.currentPose.torso as [number, number, number]));
  const stomach = useDanceStore(useShallow((state: DanceState) => state.currentPose.stomach as [number, number, number]));
  const elevation = useDanceStore(useShallow((state: DanceState) => state.currentPose.elevation as [number, number, number]));
  const leftHip = useDanceStore(useShallow((state: DanceState) => state.currentPose.leftHip as [number, number, number]));
  const rightHip = useDanceStore(useShallow((state: DanceState) => state.currentPose.rightHip as [number, number, number]));
  const leftKnee = useDanceStore(useShallow((state: DanceState) => state.currentPose.leftKnee as [number, number, number]));
  const rightKnee = useDanceStore(useShallow((state: DanceState) => state.currentPose.rightKnee as [number, number, number]));
  const leftFoot = useDanceStore(useShallow((state: DanceState) => state.currentPose.leftFoot as [number, number, number]));
  const rightFoot = useDanceStore(useShallow((state: DanceState) => state.currentPose.rightFoot as [number, number, number]));
  const chest = useDanceStore(useShallow((state: DanceState) => state.currentPose.chest as [number, number, number]));
  const neck = useDanceStore(useShallow((state: DanceState) => state.currentPose.neck as [number, number, number]));
  const setJointRotation = useDanceStore((state: DanceState) => state.setJointRotation);
  const commitHistory = useDanceStore((state: DanceState) => state.commitHistory);
  const setIsPosing = useDanceStore((state: DanceState) => state.setIsPosing);

  const disabled = isPlaying || isReadOnly;

  // Thrust: derived from elevation[2]
  const thrustVal = Math.round(Math.max(-180, Math.min(180, ((elevation?.[2] ?? 0) / MAX_THRUST) * 180)));
  // Lateral: derived from pelvis Z (pelvic tilt angle)
  const MAX_PELVIC_TILT = 30;
  const lateralVal = Math.round(Math.max(-100, Math.min(100, ((pelvis?.[2] ?? 0) / MAX_PELVIC_TILT) * 100)));
  // Vertical: derived from hip flexion (negated since squatting uses negative hip X)
  const verticalVal = Math.round(Math.max(0, Math.min(100, (-(leftHip?.[0] ?? 0) / MAX_SQUAT_A) * 100)));

  // Vertical drop: IK constraint keeps feet planted — β = a + arcsin((L1/L2)·sin(a))
  // Negative hip X = thigh tilts forward; shin compensates so foot stays at same Z position.
  const applyVertical = (val: number) => {
    const a = (val / 100) * MAX_SQUAT_A;
    const aRad = a * (Math.PI / 180);
    const kneeBeta = a + Math.asin(Math.min(LEG_RATIO * Math.sin(aRad), 1)) * RAD2DEG;
    setJointRotation('leftHip', -a, leftHip[1], leftHip[2]);
    setJointRotation('rightHip', -a, rightHip[1], rightHip[2]);
    setJointRotation('leftKnee', kneeBeta, leftKnee[1], leftKnee[2]);
    setJointRotation('rightKnee', kneeBeta, rightKnee[1], rightKnee[2]);
    setJointRotation('elevation', elevation[0], -99, elevation[2]); // clamped to grounded elevation
  };

  // Forward/back: translate elevation[2], compensate hip X (extension) and stomach X (backward lean)
  const applyThrust = (val: number) => {
    const dZ = (val / 180) * MAX_THRUST;
    const hipXComp = Math.atan2(dZ, HIP_HEIGHT_WORLD) * RAD2DEG;
    const stomachXComp = -Math.atan2(dZ, HIP_HEIGHT_WORLD * 0.4) * RAD2DEG * 0.6;
    setJointRotation('elevation', elevation?.[0] ?? 0, elevation?.[1] ?? 0, dZ);
    setJointRotation('leftHip', hipXComp, leftHip[1], leftHip[2]);
    setJointRotation('rightHip', hipXComp, rightHip[1], rightHip[2]);
    setJointRotation('stomach', stomachXComp, stomach[1], stomach[2]);
  };

  // Pelvic tilt: pelvis joint rotates around Z so hip positions actually move in world space.
  // Both hip Z counter-rotates to keep legs plumb. Dropped-side knee bends to keep foot planted.
  // Spine counter-rotates (summing to -tilt) so upper body stays upright.
  // L < 0 → right hip drops (CCW triangle); L > 0 → left hip drops (CW triangle).
  const applyLateral = (val: number) => {
    // Mirroring must be suppressed: each setJointRotation would otherwise fight its own mirror call.
    const store = useDanceStore.getState();
    const wasMirroring = store.isMirroring;
    if (wasMirroring) store.setIsMirroring(false);

    const tilt = (val / 100) * MAX_PELVIC_TILT; // degrees; negative = right hip drops
    const absTilt = Math.abs(tilt);
    const tiltRad = tilt * (Math.PI / 180);
    // Hip half-span in mannequin units (from dimensions.hipOffset[0] = 0.95)
    const drop = 0.95 * Math.abs(Math.sin(tiltRad));
    // IK: base knee bend from geometry, plus extra bend on the falling side
    const sinA = Math.min(drop / (4.22 + 3.44), 1);
    const aDeg = Math.asin(sinA) * RAD2DEG;
    const kneeBend = aDeg + Math.asin(Math.min(LEG_RATIO * sinA, 1)) * RAD2DEG + absTilt * 0.5;
    // Foot Z: rising side rolls outward (+), falling side rolls inward (-)
    // leftFootZ = -tilt * factor, rightFootZ = tilt * factor (signs work for both directions)
    // leftHip is at +X: falls when tilt<0, rises when tilt>0
    // rightHip is at -X: falls when tilt>0, rises when tilt<0
    const leftFootX  =  tilt * 0.7; // plantarflex when rising, dorsiflex when falling
    const rightFootX = -tilt * 0.7;
    const leftFootZ  =  tilt * 0.7;
    const rightFootZ = -tilt * 0.7;
    // Spine counter-rotation distributed upward, summing to -tilt so head stays upright
    const torsoZ   = -tilt * 0.5;
    const stomachZ = -tilt * 0.3;
    const chestZ   = -tilt * 0.15;
    const neckZ    = -tilt * 0.05;
    setJointRotation('pelvis', 0, 0, tilt);
    setJointRotation('leftHip', leftHip[0], leftHip[1], -tilt);
    setJointRotation('rightHip', rightHip[0], rightHip[1], -tilt);
    setJointRotation('leftKnee', tilt < 0 ? kneeBend : 0, leftKnee[1], leftKnee[2]);
    setJointRotation('rightKnee', tilt > 0 ? kneeBend : 0, rightKnee[1], rightKnee[2]);
    setJointRotation('leftFoot', leftFootX, leftFoot?.[1] ?? 0, leftFootZ);
    setJointRotation('rightFoot', rightFootX, rightFoot?.[1] ?? 0, rightFootZ);
    setJointRotation('torso', torso[0], torso[1], torsoZ);
    setJointRotation('stomach', stomach[0], stomach[1], stomachZ);
    setJointRotation('chest', chest[0], chest[1], chestZ);
    setJointRotation('neck', neck[0], neck[1], neckZ);
    setJointRotation('elevation', elevation?.[0] ?? 0, -99, elevation?.[2] ?? 0);
    if (wasMirroring) store.setIsMirroring(true);
  };

  const resetHip = () => {
    setJointRotation('pelvis', 0, 0, 0);
    setJointRotation('leftHip', 0, leftHip[1], 0);
    setJointRotation('rightHip', 0, rightHip[1], 0);
    setJointRotation('leftKnee', 0, leftKnee[1], leftKnee[2]);
    setJointRotation('rightKnee', 0, rightKnee[1], rightKnee[2]);
    setJointRotation('leftFoot', leftFoot?.[0] ?? 0, leftFoot?.[1] ?? 0, 0);
    setJointRotation('rightFoot', rightFoot?.[0] ?? 0, rightFoot?.[1] ?? 0, 0);
    setJointRotation('stomach', 0, stomach[1], 0);
    setJointRotation('torso', torso[0], 0, 0);
    setJointRotation('chest', chest[0], chest[1], 0);
    setJointRotation('neck', neck[0], neck[1], 0);
    setJointRotation('elevation', 0, -99, 0); // re-ground after straightening legs
  };

  const sliderPos = (val: number, min: number, max: number) => `calc(12px + (100% - 24px) * ${(val - min) / (max - min)})`;

  return (
    <div className={`sidebar sidebar-hip glass-panel ${isMinimized ? 'minimized' : ''}`} style={isMinimized ? { height: 'auto' } : {}}>
      <SidebarHeader title={t('sidebar.hip_controls')} name="hip" />
      {!isMinimized && (
        <div className="controls-section" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>

          <div className="control-label" style={{ marginBottom: '1px' }}><span>{t('sidebar.hip_thrust')}</span></div>
          <div className="flex-row">
            <div className="slider-wrapper">
              <input type="range" min={-180} max={180} value={thrustVal}
                onChange={(e) => applyThrust(Number(e.target.value))}
                onPointerDown={() => { commitHistory('Hip Thrust'); setIsPosing(true); }}
                onPointerUp={() => setIsPosing(false)}
                disabled={disabled}
                style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
              />
              <div className="slider-value" style={{ left: sliderPos(thrustVal, -180, 180) }}>{thrustVal}</div>
            </div>
          </div>

          <div className="control-label" style={{ marginBottom: '1px', marginTop: '4px' }}><span>{t('sidebar.hip_lateral')}</span></div>
          <div className="flex-row">
            <div className="slider-wrapper">
              <input type="range" min={-100} max={100} value={lateralVal}
                onChange={(e) => applyLateral(Number(e.target.value))}
                onPointerDown={() => { commitHistory('Hip Lateral'); setIsPosing(true); }}
                onPointerUp={() => setIsPosing(false)}
                disabled={disabled}
                style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
              />
              <div className="slider-value" style={{ left: sliderPos(lateralVal, -100, 100) }}>{lateralVal}</div>
            </div>
          </div>

          <div className="control-label" style={{ marginBottom: '1px', marginTop: '4px' }}><span>{t('sidebar.hip_vertical')}</span></div>
          <div className="flex-row">
            <div className="slider-wrapper">
              <input type="range" min={0} max={100} value={verticalVal}
                onChange={(e) => applyVertical(Number(e.target.value))}
                onPointerDown={() => { commitHistory('Hip Vertical'); setIsPosing(true); }}
                onPointerUp={() => setIsPosing(false)}
                disabled={disabled}
                style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
              />
              <div className="slider-value" style={{ left: `calc(12px + (100% - 24px) * ${verticalVal / 100})` }}>{verticalVal}</div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
            <button
              onClick={() => { commitHistory('Reset Hip'); resetHip(); }}
              disabled={disabled}
              className="icon-button mini"
              title={t('sidebar.hip_reset_tooltip')}
              style={{
                background: 'rgba(251, 146, 60, 0.15)',
                border: '1px solid rgba(251, 146, 60, 0.3)',
                borderRadius: '4px',
                height: '20px', padding: '0 6px',
                display: 'flex', alignItems: 'center', gap: '3px',
                opacity: disabled ? 0.3 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: '0.6rem', fontWeight: 'bold'
              }}
            >
              <RotateCcw size={9} color="#fb923c" />
              <span style={{ color: '#fb923c' }}>{t('sidebar.hip_reset')}</span>
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export const SidebarReset: FC = () => {
  const { t } = useTranslation();
  const isMinimized = useDanceStore((state: DanceState) => !!state.minimizedSidebars['reset']);
  const currentPose = useDanceStore((state: DanceState) => state.currentPose);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const commitHistory = useDanceStore((state: DanceState) => state.commitHistory);
  const resetPose = useDanceStore((state: DanceState) => state.resetPose);
  const resetJoint = useDanceStore((state: DanceState) => state.resetJoint);
  const selectedJoint = useDanceStore((state: DanceState) => state.selectedJoint);
  const confirmReset = useDanceStore((state: DanceState) => state.confirmReset);
  const setConfirmReset = useDanceStore((state: DanceState) => state.setConfirmReset);
  const confirmAction = useDanceStore((state: DanceState) => state.confirmAction);
  const undo = useDanceStore((state: DanceState) => state.undo);
  const redo = useDanceStore((state: DanceState) => state.redo);
  const history = useDanceStore((state: DanceState) => state.history);
  const redoStack = useDanceStore((state: DanceState) => state.redoStack);

  const selectedJointRotation = selectedJoint ? currentPose[selectedJoint] as [number, number, number] : null;
  const defaultVal = selectedJoint ? (defaultPose as any)[selectedJoint] || [0, 0, 0] : [0, 0, 0];
  const isTorsoCompoundModified = selectedJoint === 'torso' && (['pelvis', 'stomach', 'chest', 'neck', 'leftHip', 'rightHip', 'leftKnee', 'rightKnee', 'leftFoot', 'rightFoot'] as const).some(j => {
    const cur = currentPose[j] as [number, number, number];
    const def = (defaultPose as any)[j] || [0, 0, 0];
    return cur && (Math.abs(cur[0] - def[0]) > 0.001 || Math.abs(cur[1] - def[1]) > 0.001 || Math.abs(cur[2] - def[2]) > 0.001);
  });
  const isJointModified = isTorsoCompoundModified || !!(selectedJointRotation && (
    Math.abs(selectedJointRotation[0] - defaultVal[0]) > 0.001 ||
    Math.abs(selectedJointRotation[1] - defaultVal[1]) > 0.001 ||
    Math.abs(selectedJointRotation[2] - defaultVal[2]) > 0.001
  ));

  return (
    <div className={`sidebar sidebar-reset glass-panel ${isMinimized ? 'minimized' : ''}`} style={isMinimized ? { height: 'auto' } : { paddingBottom: '3px' }}>
      <SidebarHeader title={t('sidebar.reset')} name="reset" />
      {!isMinimized && (
        <div className="controls-section" style={{ display: 'flex', flexDirection: 'column' }}>
          
          <div style={{ marginTop: '2px', marginBottom: '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              {/* Undo */}
              <button 
                id="sidebar-undo-btn"
                onClick={() => undo()}
                disabled={isPlaying || history.length === 0}
                className="icon-button mini"
                title={t('sidebar.undo')}
                style={{
                  background: history.length > 0 ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.05)', 
                  width: '18px', height: '18px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: (isPlaying || history.length === 0) ? 0.3 : 1, cursor: (isPlaying || history.length === 0) ? 'not-allowed' : 'pointer',
                  border: history.length > 0 ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid transparent'
                }}
              >
                <Undo size={9} color={history.length > 0 ? '#38bdf8' : '#94a3b8'} />
              </button>

              {/* Redo */}
              <button 
                id="sidebar-redo-btn"
                onClick={() => redo()}
                disabled={isPlaying || redoStack.length === 0}
                className="icon-button mini"
                title={t('sidebar.redo')}
                style={{
                  background: redoStack.length > 0 ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.05)', 
                  width: '18px', height: '18px', borderRadius: '4px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: (isPlaying || redoStack.length === 0) ? 0.3 : 1, cursor: (isPlaying || redoStack.length === 0) ? 'not-allowed' : 'pointer',
                  border: redoStack.length > 0 ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid transparent'
                }}
              >
                <Redo size={9} color={redoStack.length > 0 ? '#38bdf8' : '#94a3b8'} />
              </button>

              {/* Reset Joint */}
              <button 
                id="tutorial-reset-joint"
                onClick={() => {
                  if (selectedJoint) {
                    resetJoint(selectedJoint);
                  }
                }} 
                className="icon-button mini"
                disabled={!selectedJoint || isPlaying}
                title={t('sidebar.reset_joint_tooltip')}
                style={{
                  background: isJointModified ? 'rgba(251, 146, 60, 0.2)' : 'rgba(255, 255, 255, 0.05)', 
                  width: 'auto', minWidth: '40px', height: '18px', borderRadius: '4px', padding: '0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px',
                  opacity: (!selectedJoint || isPlaying) ? 0.3 : 1, cursor: (!selectedJoint || isPlaying) ? 'not-allowed' : 'pointer',
                  border: isJointModified ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid transparent',
                  fontSize: '0.55rem', fontWeight: 'bold'
                }}
              >
                <RotateCcw size={8} color={isJointModified ? '#fb923c' : '#94a3b8'} />
                <span style={{ color: isJointModified ? '#fb923c' : '#94a3b8' }}>{t('sidebar.joint')}</span>
              </button>

              {/* Reset Camera */}
              <button 
                onClick={() => useDanceStore.getState().resetCamera()}
                className="icon-button mini"
                title={t('sidebar.reset_camera')}
                style={{
                  background: 'rgba(56, 189, 248, 0.1)', 
                  width: 'auto', minWidth: '40px', height: '18px', borderRadius: '4px', padding: '0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px',
                  border: '1px solid rgba(56, 189, 248, 0.2)',
                  fontSize: '0.55rem', fontWeight: 'bold'
                }}
              >
                <Compass size={8} color="#38bdf8" />
                <span style={{ color: '#38bdf8' }}>{t('sidebar.camera')}</span>
              </button>

              {/* Spacer to push Pose button to the right */}
              <div style={{ flex: 1 }} />

              {/* Reset Pose Combined Control */}
              <div className="reset-combined-control" style={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(244, 114, 182, 0.08)',
                border: '1px solid rgba(244, 114, 182, 0.2)',
                borderRadius: '6px',
                height: '18px',
                padding: '0 2px',
                opacity: isPlaying ? 0.3 : 1,
                gap: '2px',
                width: 'fit-content'
              }}>
                <button 
                  id="tutorial-reset-pose"
                  onClick={() => confirmReset ? confirmAction({ title: t('sidebar.reset_pose_title'), message: t('sidebar.reset_pose_message'), onConfirm: () => resetPose() }) : resetPose()}
                  disabled={isPlaying}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    padding: '0 2px 0 0', 
                    color: '#f472b6', 
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    fontSize: '0.6rem',
                    fontWeight: 'bold'
                  }}
                  title={t('sidebar.reset_pose_title')}
                >
                  <RotateCcw size={8} />
                  <span>{t('sidebar.pose')}</span>
                </button>
                <div style={{ width: '1px', height: '10px', background: 'rgba(244, 114, 182, 0.2)' }} />
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '0 0 0 2px' }}>
                  <div style={{ position: 'relative', width: '9px', height: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <input 
                      id="sidebar-confirm-reset-checkbox"
                      type="checkbox" checked={confirmReset} 
                      onChange={(e) => setConfirmReset(e.target.checked)} 
                      disabled={isPlaying}
                      style={{ 
                        appearance: 'none', 
                        width: '100%', 
                        height: '100%', 
                        margin: 0,
                        border: '1px solid rgba(244, 114, 182, 0.5)', 
                        borderRadius: '2px', 
                        background: 'transparent',
                        cursor: 'pointer'
                      }}
                    />
                    {confirmReset && (
                      <div style={{ 
                        position: 'absolute',
                        width: '4px', 
                        height: '4px', 
                        background: 'rgba(244, 114, 182, 0.9)', 
                        borderRadius: '1px',
                        pointerEvents: 'none'
                      }} />
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


